import { z } from 'zod';

/** Mirrors `app_key` in the DB enum and `@RequireApp('<app>')`. */
export const APP_KEYS = [
  'admin',
  'library',
  'ag5',
  'finances',
  'brain',
  'calories',
  'boxer',
  'recipes',
  'checklists',
] as const;
export const appKeySchema = z.enum(APP_KEYS);
export type AppKey = z.infer<typeof appKeySchema>;

/**
 * Who a table belongs to: an app, or `core` for the platform tables every app
 * shares. Every table is named `<owner>_<name>`, which is how the admin
 * overview attributes database size.
 */
export const TABLE_OWNERS = ['core', ...APP_KEYS] as const;
export const tableOwnerSchema = z.enum(TABLE_OWNERS);
export type TableOwner = z.infer<typeof tableOwnerSchema>;

/** Mirrors `locale` in the DB enum. The UI languages a user can choose. */
export const LOCALES = ['pl', 'en'] as const;
export const localeSchema = z.enum(LOCALES);
export type Locale = z.infer<typeof localeSchema>;

/**
 * Languages each app's UI exists in. The first entry is the fallback: an app
 * that lacks the user's language renders in it. Translating an app means adding
 * its dictionary and extending its entry here.
 */
export const APP_LANGUAGES = {
  admin: ['en', 'pl'],
  library: ['en', 'pl'],
  ag5: ['en', 'pl'],
  finances: ['en', 'pl'],
  brain: ['en', 'pl'],
  calories: ['pl', 'en'],
  boxer: ['pl', 'en'],
  recipes: ['pl', 'en'],
  checklists: ['pl', 'en'],
} as const satisfies Record<AppKey, readonly [Locale, ...Locale[]]>;

/** Mirrors `ICON_KEYS` in the AG5 useful-links module. */
export const ICON_KEYS = [
  'github',
  'aws',
  'youtrack',
  'nuclino',
  'link',
] as const;
export const iconKeySchema = z.enum(ICON_KEYS);
export type LinkIconKey = z.infer<typeof iconKeySchema>;

export const MIN_PASSWORD_LENGTH = 8;

/**
 * Every reason the API gives for a 4xx/5xx. The UI turns the code into text in
 * the effective language (`apps/web/src/i18n/errors.ts`); the body's `message`
 * is English diagnostics for logs and curl and never reaches a user. Zod rules
 * in `inputs.ts` use these as their `message`, so a failed validation carries
 * one too.
 */
export const ERROR_CODES = [
  'invalid_request',
  // auth
  'invalid_credentials',
  'not_authenticated',
  'app_access_required',
  'wrong_current_password',
  'current_password_required',
  'password_too_short',
  'passwords_mismatch',
  // admin
  'user_not_found',
  'login_required',
  'login_taken',
  'cannot_delete_self',
  // ag5
  'link_not_found',
  'label_required',
  'url_invalid',
  // finances
  'wallet_not_found',
  'goal_not_found',
  'name_required',
  'amount_invalid',
  'target_amount_invalid',
  'month_required',
  // brain
  'note_not_found',
  'note_required',
  'question_required',
  'brain_unreachable',
  // calories
  'entry_not_found',
  'product_not_found',
  'meal_not_found',
  'duplicate_product',
  'dish_not_found',
  'duplicate_dish',
  'quantity_below_one',
  'quantity_at_max',
  'meals_stale',
  'product_name_required',
  'meal_name_required',
  'grams_invalid',
  'kcal_invalid',
  'quantity_invalid',
  'daily_limit_invalid',
  'day_invalid',
  'weight_invalid',
  'weight_not_found',
  'weight_date_taken',
  // recipes
  'recipe_not_found',
  'duplicate_recipe',
  // checklists
  'checklist_not_found',
  'checklist_item_not_found',
  'duplicate_checklist',
  'checklist_child_invalid',
  'checklist_children_required',
  'checklist_group_not_found',
  'duplicate_checklist_group',
  'checklist_group_not_empty',
  'checklist_group_invalid',
  'checklist_order_stale',
] as const;
export const errorCodeSchema = z.enum(ERROR_CODES);
export type ErrorCode = z.infer<typeof errorCodeSchema>;

/** Every 4xx/5xx in this API: a code the UI translates, plus diagnostics. */
export const errorSchema = z.object({
  code: errorCodeSchema,
  message: z.string(),
});
export type ApiError = z.infer<typeof errorSchema>;

/**
 * Response sets every guarded route repeats. Spread one into `responses` rather
 * than listing the codes per route — a route that forgets 403 type-errors on the
 * handler instead of shipping an undeclared status.
 */
export const guardErrors = { 401: errorSchema, 403: errorSchema } as const;
/** Guarded, plus request validation. */
export const bodyErrors = { 400: errorSchema, ...guardErrors } as const;
/** Guarded, plus "that id isn't yours or doesn't exist". */
export const targetErrors = { ...guardErrors, 404: errorSchema } as const;
/** Guarded, validated, and addressed by id. */
export const bodyTargetErrors = { ...bodyErrors, 404: errorSchema } as const;

/**
 * Guarded, validated, plus "a model we depend on didn't answer". Only the brain
 * app has an upstream; every other route is this database and nothing more.
 */
export const bodyUpstreamErrors = { ...bodyErrors, 502: errorSchema } as const;
/** Guarded, validated, addressed by id, and dependent on an upstream. */
export const bodyTargetUpstreamErrors = {
  ...bodyTargetErrors,
  502: errorSchema,
} as const;

export const idParams = z.object({ id: z.string().uuid() });

/** A `timestamp with time zone` column once JSON has flattened it. */
export const isoDateTime = z.string().datetime();

/** A `date` column — drizzle hands these back as `YYYY-MM-DD` strings. */
export const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/**
 * A `numeric(14,2)` column. These stay strings end to end: `pg` returns them as
 * strings and parsing them into JS numbers silently rounds once the values get
 * large enough. Format at render time, never before.
 */
export const decimalString = z.string().regex(/^-?\d+(\.\d{1,2})?$/);
